import React, { useState, useContext, ComponentProps } from 'react'
import { Form as FormHelper } from 'helpers/form'
import { TournamentBiographyRecord } from "data/TournamentBiographyRecord";
import { AppContext } from 'App';
import { useFormik } from 'formik';
import FormItem from 'components/utils/FormItem';
import { Col, Form } from 'react-bootstrap';
import { ItemWrapper, OffsetedContainer } from 'styles/common';
import RichTextEditor from 'components/RichTextEditor';
import { FormContext } from 'components/utils/Form.context';
import Footer from 'components/utils/Form/Footer';
import ImageGalleryPickerParent from 'components/utils/ImageGalleryPicker/ImageGalleryPickerParent';
import ImageGallery from 'components/utils/Image/ImageGallery';

const SubmitKeys = [
  'facebook',
  'images',
  'instagram',
  'perex',
  'team_id',
  'text',
  'twich',
  'twitter',
] as Array<keyof TournamentBiographyRecord>

const TournamentBiographyForm: React.FC<FormHelper.Props<TournamentBiographyRecord> & { tournamentId?: string }> = (p) => {
  const { db, alert, history, modal } = useContext(AppContext)

  const [form, setForm] = useState<{
    data?: TournamentBiographyRecord
    state: FormHelper.FormState
  }>({ data: p.data, state: p.state ?? 'new' })

  const opt = {
    alert: {
      manager: alert,
      texts: {
        form: 'Tournament Biography',
      },
    },
    history: history,
    lock: {
      callback: p.locked?.callback,
    },
  }

  const formik = useFormik({
    initialValues: {
      ...form.data,

      id: form.data?.id,
      tournament_id: p.tournamentId
    },
    onSubmit: async (values) => {
      if (!db?.tournamentBiographies) return

      try{
        values.id = form?.data?.id

        const data = await FormHelper.submitChanges<TournamentBiographyRecord>({
          action: form.state === 'new' ? 'create' : 'update',
          keys: SubmitKeys,
          data: Object.assign(new TournamentBiographyRecord(), values ?? {}),
          repository: db.tournamentBiographies,
          optional: opt,
        })

        if (data?.id) {
          setForm({ data: data, state: 'existing' })

        }

        data && p.submitCallback?.(data)
      } catch (err) {
        console.error(err)
      }
    },
  })

  //#region Form Components
  const _Facebook = (
    <FormItem
      label={<Form.Label>Facebook</Form.Label>}
      input={
        <>
          <Form.Control
            name={'facebook' as keyof TournamentBiographyRecord}
            value={formik.values.facebook ?? ''}
            onChange={formik.handleChange}
            readOnly={p.locked?.value}
          />
        </>
      }
    />
  )

  const _Instagram = (
    <FormItem
      label={<Form.Label>Instagram</Form.Label>}
      input={
        <>
          <Form.Control
            name={'instagram' as keyof TournamentBiographyRecord}
            value={formik.values.instagram ?? ''}
            onChange={formik.handleChange}
            readOnly={p.locked?.value}
          />
        </>
      }
    />
  )

  const _Twich = (
    <FormItem
      label={<Form.Label>Twich</Form.Label>}
      input={
        <>
          <Form.Control
            name={'twich' as keyof TournamentBiographyRecord}
            value={formik.values.twich ?? ''}
            onChange={formik.handleChange}
            readOnly={p.locked?.value}
          />
        </>
      }
    />
  )

  const _Twitter = (
    <FormItem
      label={<Form.Label>Twitter</Form.Label>}
      input={
        <>
          <Form.Control
            name={'twitter' as keyof TournamentBiographyRecord}
            value={formik.values.twitter ?? ''}
            onChange={formik.handleChange}
            readOnly={p.locked?.value}
          />
        </>
      }
    />
  )

  const _Perex = (
    <FormItem
      label={<Form.Label>Perex</Form.Label>}
      input={
        <>
          <Form.Control
            name={'perex' as keyof TournamentBiographyRecord}
            value={formik.values.perex ?? ''}
            onChange={formik.handleChange}
            readOnly={p.locked?.value}
          />
        </>
      }
    />
  )

  const _Text = (
    <FormItem
      label={<Form.Label>Text</Form.Label>}
      input={
        <>
          <ItemWrapper />
          <RichTextEditor
            text={formik.values.text ?? ''}
            readonly={p.locked?.value}
            onChange={(text?: string) => {
              formik.setFieldValue('text' as keyof TournamentBiographyRecord, text)
            }}
          />
        </>
      }
    />
  )

  const _Image = (
    <ImageGallery
      inputName={'images' as keyof TournamentBiographyRecord}
      imageUrl={formik.values.images ?? ''}
      isLocked={p.locked?.value}
      labelName='Bio Photo'
      onImageClickCallback={() => {
        modal?.open?.({
          content: (
            <>
              <ImageGalleryPickerParent
                actualDir={'teams'}
                actualImageString={formik.values.images ?? ''}
                useMultipleSelect={{
                  divider: '|',
                }}
                onSubmitCallback={(actualImageString: string) => {
                  formik.setFieldValue(
                    'images' as keyof TournamentBiographyRecord,
                    actualImageString
                  )

                  modal?.close?.()
                }}
              />
            </>
          ),
        })
      }}
      onUpdateClickCallback={(val?: string) =>
        formik.setFieldValue('images' as keyof TournamentBiographyRecord, val)
      }
    />
  )

  //#endregion

  const _ComputeFooterProps = (): ComponentProps<typeof Footer> => {
    if (form.state === 'new')
      return {
        useSave: {},
      }

    return { useSave: {}, locked: p.locked?.value }
  }

  return (
    <OffsetedContainer>
      <FormContext.Provider
        value={{
          submit: formik.submitForm,
        }}
      >
        <Form noValidate onSubmit={formik.handleSubmit}>
          <Footer {..._ComputeFooterProps()} />

          <Form.Row>
            <Col lg={12}>
              <ItemWrapper />
              <Form.Row>
                <Col as={ItemWrapper}>{_Facebook}</Col>
              </Form.Row>
              <Form.Row>
                <Col as={ItemWrapper}>{_Instagram}</Col>
              </Form.Row>
              <Form.Row>
                <Col as={ItemWrapper}>{_Twich}</Col>
              </Form.Row>
              <Form.Row>
                <Col as={ItemWrapper}>{_Twitter}</Col>
              </Form.Row>
              <Form.Row>
                <Col as={ItemWrapper}>{_Perex}</Col>
              </Form.Row>
              <Form.Row>
                <Col as={ItemWrapper}>{_Text}</Col>
              </Form.Row>
              <ItemWrapper />
              <Form.Row>
                <Col as={ItemWrapper}>{_Image}</Col>
              </Form.Row>
            </Col>
          </Form.Row>
        </Form>
      </FormContext.Provider>
    </OffsetedContainer>
  )
}

export default TournamentBiographyForm
